import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useGoogleDriveConnection from '../../hooks/useGoogleDriveConnection';
import { useUploadQueue } from '../../context/UploadQueueContext';

function formatBytes(bytes) {
  const value = Number(bytes) || 0;
  if (value < 1024) return `${value} B`;
  const kb = value / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

export default function GoogleDriveBrowser({ siteId }) {
  const { status, connect, disconnect, refresh } = useGoogleDriveConnection(siteId);
  const { enqueueGoogleDriveFiles } = useUploadQueue();
  const [folderId, setFolderId] = useState('root');
  const [items, setItems] = useState([]);
  const [path, setPath] = useState([{ id: 'root', name: 'My Drive' }]);
  const [selected, setSelected] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const fetchFolder = useCallback(async (targetFolderId) => {
    if (!siteId || !token) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `/api/googledrive/list?site=${encodeURIComponent(siteId)}&folderId=${encodeURIComponent(targetFolderId || 'root')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        let msg = `Failed to load folder (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.error || msg;
          if (data?.details?.reason) msg += ` (${data.details.reason})`;
        } catch (err) {
          const text = await res.text();
          if (text) msg = text;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setError(err?.message || 'Failed to load Google Drive folder');
    } finally {
      setLoading(false);
    }
  }, [siteId, token]);

  useEffect(() => {
    if (!status.connected) return;
    fetchFolder(folderId);
  }, [status.connected, fetchFolder, folderId]);

  const visibleIds = useMemo(() => items.map((i) => i.id).filter(Boolean), [items]);
  const allSelected = useMemo(() => visibleIds.length > 0 && visibleIds.every((id) => selected.has(id)), [visibleIds, selected]);

  const selectedItems = useMemo(() => {
    const map = new Map(items.map((i) => [i.id, i]));
    return Array.from(selected).map((id) => map.get(id)).filter(Boolean);
  }, [items, selected]);

  const canUpload = status.connected && selectedItems.length > 0;

  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  };

  const openFolder = (item) => {
    if (!item?.isFolder) return;
    setFolderId(item.id);
    setSelected(new Set());
    setPath((prev) => [...prev, { id: item.id, name: item.name || 'Folder' }]);
  };

  const goToPathIndex = (idx) => {
    setPath((prev) => {
      const next = prev.slice(0, idx + 1);
      const target = next[next.length - 1];
      setFolderId(target?.id || 'root');
      setSelected(new Set());
      return next;
    });
  };

  const resolveAndEnqueue = async () => {
    if (!siteId || !token) return;
    if (!selectedItems.length) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/googledrive/resolve', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          siteId,
          items: selectedItems.map((item) => ({ id: item.id, isFolder: !!item.isFolder })),
        }),
      });
      if (!res.ok) {
        let msg = `Resolve failed (${res.status})`;
        try {
          const data = await res.json();
          msg = data?.error || msg;
          if (data?.details?.reason) msg += ` (${data.details.reason})`;
        } catch (err) {
          const text = await res.text();
          if (text) msg = text;
        }
        throw new Error(msg);
      }
      const data = await res.json();
      const files = Array.isArray(data.files) ? data.files : [];
      if (files.length) {
        enqueueGoogleDriveFiles(siteId, files);
        setSelected(new Set());
      }
    } catch (err) {
      setError(err?.message || 'Failed to prepare upload');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg text-white">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="font-semibold">Google Drive</div>
          {status.connected ? (
            <span className="text-xs px-2 py-1 rounded bg-green-700">Connected</span>
          ) : (
            <span className="text-xs px-2 py-1 rounded bg-gray-700">Not connected</span>
          )}
          {status.needs_reauth && (
            <span className="text-xs px-2 py-1 rounded bg-yellow-700">Re-auth required</span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {!status.connected ? (
            <button
              className="px-3 py-1 bg-indigo-600 rounded text-white"
              onClick={connect}
              disabled={!siteId}
            >
              Connect Google Drive
            </button>
          ) : (
            <>
              <button
                className="px-3 py-1 bg-gray-700 rounded text-white"
                onClick={() => refresh()}
              >
                Refresh status
              </button>
              <button
                className="px-3 py-1 bg-red-700 rounded text-white"
                onClick={disconnect}
              >
                Disconnect
              </button>
            </>
          )}
        </div>
      </div>

      {status.last_error && (
        <div className="mt-2 text-xs text-red-200">
          Last error: {status.last_error}
        </div>
      )}

      {status.connected && (
        <>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm text-gray-200 flex flex-wrap items-center gap-2">
              <span className="text-gray-400">Path:</span>
              {path.map((seg, idx) => (
                <button
                  key={`${seg.id}-${idx}`}
                  className="text-indigo-300 hover:text-indigo-200 underline"
                  onClick={() => goToPathIndex(idx)}
                >
                  {seg.name}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                className="px-3 py-1 bg-gray-700 rounded text-white"
                onClick={toggleSelectAll}
                disabled={!items.length || loading}
              >
                {allSelected ? 'Select none' : 'Select all'}
              </button>
              {canUpload && (
                <button
                  className="px-3 py-1 bg-indigo-600 rounded text-white"
                  onClick={resolveAndEnqueue}
                  disabled={loading}
                >
                  Upload ({selectedItems.length})
                </button>
              )}
            </div>
          </div>

          {error && <div className="mt-2 text-sm text-red-300">{error}</div>}
          {loading && <div className="mt-2 text-sm text-gray-300">Loading…</div>}

          {!loading && items.length === 0 && (
            <div className="mt-2 text-sm text-gray-300">This folder is empty.</div>
          )}

          {!loading && items.length > 0 && (
            <div className="mt-3 bg-gray-900 rounded overflow-hidden">
              <div className="grid grid-cols-12 gap-2 px-3 py-2 text-xs text-gray-300 border-b border-gray-800">
                <div className="col-span-1">Sel</div>
                <div className="col-span-7">Name</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-2 text-right">Size</div>
              </div>
              {items.map((item) => {
                const disabled = item.isGoogleDoc && !item.isFolder;
                const isChecked = selected.has(item.id);
                const typeLabel = item.isFolder ? 'Folder' : (item.mimeType || 'file');
                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 gap-2 px-3 py-2 text-sm border-b border-gray-800 hover:bg-gray-800"
                  >
                    <div className="col-span-1 flex items-center">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        disabled={loading || disabled}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (checked) next.add(item.id);
                            else next.delete(item.id);
                            return next;
                          });
                        }}
                      />
                    </div>
                    <div className="col-span-7 min-w-0">
                      <button
                        className={`truncate text-left w-full ${item.isFolder ? 'text-indigo-300 hover:text-indigo-200 underline' : disabled ? 'text-gray-500' : 'text-gray-100'}`}
                        onClick={() => openFolder(item)}
                        disabled={!item.isFolder}
                        title={item.name}
                      >
                        {item.name || 'Untitled'}
                      </button>
                      {disabled && (
                        <div className="text-xs text-gray-500">Google Docs/Sheets/Slides not supported yet</div>
                      )}
                    </div>
                    <div className="col-span-2 text-xs text-gray-300 flex items-center">
                      {typeLabel}
                    </div>
                    <div className="col-span-2 text-xs text-gray-300 flex items-center justify-end">
                      {item.isFolder ? '—' : formatBytes(item.size)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
